'use client';

import { sessionCookieFetch } from '@sourcepilot/ui/framework/data/dag-sync/organisation-change';
import { sessionChangeFailed, sessionChangeRefused } from '@sourcepilot/ui/framework/data/session-change-refusal';

/**
 * Hook for managing user impersonation
 *
 * Allows admins to view the system as another user for debugging/support.
 * - Impersonation shows the impersonated user's data (no security bypass)
 * - Write operations are disabled by default (safety measure)
 * - impersonate_write mode can be enabled for write access
 */

import { useCallback } from 'react';
import { useSimpleAuth } from '@sourcepilot/ui/shell/auth/use-simple-auth';

const SOURCEPILOT_SERVER_URL = process.env.NEXT_PUBLIC_SOURCEPILOT_URL;

export function useImpersonation() {
  const { user, refetch } = useSimpleAuth();

  const isImpersonating = user?.impersonating === true;
  // whom this session views as, by public profile (VC-Authz 9db79b501)
  const impersonatedPerson = user?.impersonated_person ?? null;
  const canWrite = user?.impersonate_write === true;
  const isAdmin = user?.is_admin === true;

  // Start impersonating a user, named by their User node id: the sign-in
  // identifier never reaches the browser (VC-Authz 9db79b501)
  const startImpersonation = useCallback(async (userId: string, allowWrite: boolean = false) => {
    if (!isAdmin || !userId) return false;

    try {
      const response = await sessionCookieFetch(
        `${SOURCEPILOT_SERVER_URL}/auth/impersonate`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, allow_write: allowWrite }),
        }
      );

      if (response.ok) {
        // Force a page reload to refresh all data with impersonated user's context
        // This ensures products, chats, and workspace data are fetched fresh
        window.location.reload();
        return true;
      }
      await sessionChangeRefused(response, 'Impersonation did not start.');
      return false;
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      sessionChangeFailed(error, 'Impersonation did not start.');
      return false;
    }
  }, [isAdmin]);

  // Stop impersonating
  const stopImpersonation = useCallback(async () => {
    if (!isImpersonating) return false;

    try {
      const response = await sessionCookieFetch(
        `${SOURCEPILOT_SERVER_URL}/auth/impersonate/stop`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (response.ok) {
        await refetch(); // Refresh to get original user data
        // Force a page reload to refresh all data with original user's context
        window.location.reload();
        return true;
      }
      await sessionChangeRefused(response, 'Impersonation did not stop.');
      return false;
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      sessionChangeFailed(error, 'Impersonation did not stop.');
      return false;
    }
  }, [isImpersonating, refetch]);

  // Toggle write mode during impersonation
  const setWriteMode = useCallback(async (enabled: boolean) => {
    if (!isImpersonating) return false;

    try {
      const response = await sessionCookieFetch(
        `${SOURCEPILOT_SERVER_URL}/auth/impersonate/write-mode?enabled=${enabled}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (response.ok) {
        await refetch();
        return true;
      }
      await sessionChangeRefused(response, 'Impersonation write mode was not changed.');
      return false;
    } catch (error) {
      console.error('Failed to toggle impersonation write mode:', error);
      sessionChangeFailed(error, 'Impersonation write mode was not changed.');
      return false;
    }
  }, [isImpersonating, refetch]);

  return {
    isImpersonating,
    impersonatedPerson,
    canWrite,
    isAdmin,
    startImpersonation,
    stopImpersonation,
    setWriteMode,
  };
}
